import { HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { filter, map, Observable, of } from 'rxjs';

import { environment } from '../../../environments/environment';
import { Attachment } from '../../models/attachment';
import { UtilityService } from '../utility/utility.service';

/** Progress ticks followed by the stored file, so a caller can drive a per-file bar. */
export type AttachmentUploadEvent =
  | { type: 'progress'; percent: number }
  | { type: 'uploaded'; attachment: Attachment };

@Injectable({
  providedIn: 'root',
})
export class AttachmentService {
  private utilityService = inject(UtilityService);

  attachmentEndpoint = `${environment.testHubApiUrl}/v1/Attachment`;

  /**
   * Upload one file ahead of the result it belongs to — one call per file, since the
   * endpoint takes a single `file` part. The returned attachmentGuid goes in the result
   * POST's attachmentIds; an upload that is never submitted is swept from storage by the
   * API, so an abandoned form leaves nothing behind.
   *
   * Unsubscribing aborts the request, which is how the drawer cancels an upload the
   * tester removed mid-flight.
   */
  uploadAttachment(userId: number, file: File): Observable<AttachmentUploadEvent> {
    if (environment.useMockData) {
      const mockEvents: AttachmentUploadEvent[] = [
        { type: 'progress', percent: 100 },
        {
          type: 'uploaded',
          attachment: {
            attachmentGuid: crypto.randomUUID(),
            fileName: file.name,
            contentType: file.type,
            fileSizeBytes: file.size,
          },
        },
      ];
      return of(...mockEvents);
    }

    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.utilityService
      .postFileRequest<Attachment>(`${this.attachmentEndpoint}/${userId}`, formData)
      .pipe(
        map((event): AttachmentUploadEvent | null => {
          if (event.type === HttpEventType.UploadProgress) {
            // total is absent when the length isn't known; hold at 0 rather than NaN.
            const percent = event.total ? Math.round((event.loaded / event.total) * 100) : 0;
            return { type: 'progress', percent };
          }

          if (event.type === HttpEventType.Response) {
            const attachment = event.body?.result;
            // A 2xx with no attachment means no guid to submit; fail loudly rather than
            // leaving the file stuck at "uploading" forever.
            if (!attachment) throw new Error('Upload succeeded but returned no attachment.');
            return { type: 'uploaded', attachment };
          }

          return null;
        }),
        filter((event): event is AttachmentUploadEvent => event !== null),
      );
  }

  /**
   * Drop a file the tester removed from the drawer before submitting. The API refuses to
   * delete an attachment already tied to a result, so this is only ever the pre-submit
   * case.
   *
   * The API's sweeper would collect an abandoned upload eventually; calling this keeps a
   * removed file from sitting in storage until then.
   */
  deleteAttachment(userId: number, attachmentGuid: string): Observable<void> {
    if (environment.useMockData) return of(undefined);

    return this.utilityService.deleteRequest<void>(
      `${this.attachmentEndpoint}/${userId}/${attachmentGuid}`,
    );
  }
}
